import React from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';

export const CartDrawer = () => {
  const { items, isOpen, setCartOpen, removeFromCart, updateQuantity, cartTotal, cartCount } = useCart();

  return (
    <Sheet open={isOpen} onOpenChange={setCartOpen}>
      <SheetContent className="w-full sm:max-w-lg" data-testid="cart-drawer">
        <SheetHeader className="border-b pb-4">
          <SheetTitle className="font-heading text-xl flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-brand-orange" />
            Your Cart ({cartCount})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[60vh] text-center" data-testid="empty-cart">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <ShoppingBag className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="font-heading font-bold text-xl text-gray-800 mb-2">Your cart is empty</h3>
            <p className="text-gray-500 mb-6">Looks like you haven't added any toys yet!</p>
            <Button onClick={() => setCartOpen(false)} className="btn-primary" asChild>
              <Link to="/">Start Shopping</Link>
            </Button>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4" data-testid="cart-items">
              <AnimatePresence>
                {items.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex gap-4 p-4 bg-gray-50 rounded-2xl"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-xl"
                    />
                    <div className="flex-1">
                      <h4 className="font-heading font-semibold text-gray-800">{item.name}</h4>
                      <p className="text-brand-orange font-bold">${item.price.toFixed(2)}</p>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-1 rounded-full bg-white border hover:bg-gray-100 transition-colors"
                          data-testid={`decrease-qty-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-medium w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-1 rounded-full bg-white border hover:bg-gray-100 transition-colors"
                          data-testid={`increase-qty-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Cart Summary */}
            <div className="border-t pt-4 space-y-4" data-testid="cart-summary">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-bold text-lg">${cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Shipping</span>
                <span className="text-brand-green font-medium">
                  {cartTotal >= 50 ? 'FREE' : '$5.99'}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-heading font-bold text-lg">Total</span>
                <span className="font-bold text-xl text-brand-orange">
                  ${(cartTotal + (cartTotal >= 50 ? 0 : 5.99)).toFixed(2)}
                </span>
              </div>

              {cartTotal < 50 && (
                <p className="text-sm text-center text-gray-500 bg-brand-orange-light p-2 rounded-lg">
                  Add ${(50 - cartTotal).toFixed(2)} more for FREE shipping!
                </p>
              )}

              <div className="space-y-2">
                <Button 
                  className="w-full btn-primary" 
                  onClick={() => setCartOpen(false)}
                  asChild
                  data-testid="checkout-btn"
                >
                  <Link to="/checkout">Proceed to Checkout</Link>
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setCartOpen(false)}
                  data-testid="continue-shopping-btn"
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
